import {
  ActivatedRouteSnapshot,
  BaseRouteReuseStrategy,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';
import {notNull} from './shared/utils/utils';

function doesRouteMatch(
  activatedRoute: ActivatedRouteSnapshot,
  path: string,
  parentPath?: string,
): boolean {
  if (parentPath) {
    return (
      activatedRoute.routeConfig?.path === path &&
      activatedRoute.parent?.routeConfig?.path === parentPath
    );
  }
  return activatedRoute.routeConfig?.path === path;
}

function findMatchingRoute(
  activatedRoute: ActivatedRouteSnapshot,
  cacheableRoutes: CacheableRoute[],
): CacheableRoute | null {
  return (
    cacheableRoutes.find(route => doesRouteMatch(activatedRoute, route.path, route.parentPath)) ??
    null
  );
}

function shouldBeCached(
  activatedRoute: ActivatedRouteSnapshot,
  cacheableRoutes: CacheableRoute[],
): boolean {
  return findMatchingRoute(activatedRoute, cacheableRoutes) !== null;
}

function findCachedHandle(
  routeName: CacheableRouteName,
  handles: RouteHandle[],
): RouteHandle | null {
  return handles.find(handle => handle.name === routeName) || null;
}

function hasCachedHandle(routeName: CacheableRouteName, handles: RouteHandle[]): boolean {
  return findCachedHandle(routeName, handles) !== null;
}

export const cachedRoutes = [
  '/templates/overview',
  '/content',
  '/builder/overview',
  '/deliveries',
  '/users',
] as const;

export type CacheableRouteName = (typeof cachedRoutes)[number];

const routeDefinitions: {[key in CacheableRouteName]: Omit<CacheableRoute, 'name'> & {name: key}} =
  {
    '/templates/overview': {path: 'overview', parentPath: 'templates', name: '/templates/overview'},
    '/content': {path: 'content', name: '/content'},
    '/builder/overview': {path: 'overview', parentPath: 'builder', name: '/builder/overview'},
    '/deliveries': {path: 'deliveries', name: '/deliveries'},
    '/users': {path: 'users', name: '/users'},
  };

const cacheableRoutes = Object.values(routeDefinitions);

interface RouteHandle {
  name: string;
  handle: DetachedRouteHandle;
}

interface CacheableRoute {
  path: string;
  parentPath?: string;
  name: CacheableRouteName;
}

export class CustomRouteReuseStrategy extends BaseRouteReuseStrategy implements RouteReuseStrategy {
  handles: RouteHandle[] = [];

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return shouldBeCached(route, cacheableRoutes);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (handle === null) {
      return;
    }
    const matchingRoute = findMatchingRoute(route, cacheableRoutes);

    if (matchingRoute !== null) {
      this.handles.push({
        name: matchingRoute.name,
        handle,
      });
    }
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (!shouldBeCached(route, cacheableRoutes)) {
      return false;
    }

    const cacheableRoute = findMatchingRoute(route, cacheableRoutes);
    return hasCachedHandle(notNull(cacheableRoute).name, this.handles);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const cacheableRoute = findMatchingRoute(route, cacheableRoutes);
    const cachedHandle = findCachedHandle(notNull(cacheableRoute).name, this.handles);

    if (cachedHandle === undefined || cachedHandle === null) {
      return null; // Should not occur. Also, returning null apparently produces an error.
    }

    return cachedHandle.handle;
  }
}
